// src/pages/DetalleSolicitud.jsx
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useToast } from "../components/Toast/useToast.jsx";
import { useAuth } from "../contexts/AuthContext";
import { db } from "../firebase";
import { doc, getDoc, deleteDoc } from "firebase/firestore";

export default function DetalleSolicitud() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { user, isCompanyAdmin, isSuperAdmin } = useAuth();

  const [solicitud, setSolicitud] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);

  const isAdmin = isCompanyAdmin() || isSuperAdmin();
  // Usuario puede editar si la solicitud es suya y está en "enviada"
  const canEdit = (sol) => isAdmin || (sol?.requesterId === user?.uid && sol?.status === 'enviada');
  const canDelete = isAdmin;

  // Cargar solicitud
  useEffect(() => {
    loadSolicitud();
  }, [id]);

  const loadSolicitud = async () => {
    if (!id) return;

    try {
      setLoading(true);
      
      const docRef = doc(db, "work_requests", id);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        showToast({ type: "error", message: "Solicitud no encontrada" });
        navigate('/historial-solicitudes');
        return;
      }

      const data = { id: docSnap.id, ...docSnap.data() };

      // Admins pueden ver cualquier solicitud; usuarios solo las suyas
      const esAdmin = isCompanyAdmin() || isSuperAdmin();
      if (!esAdmin && data.requesterId !== user?.uid) {
        showToast({ type: "error", message: "No tienes permisos para ver esta solicitud" });
        navigate('/historial-solicitudes');
        return;
      }

      setSolicitud(data);
    } catch (error) {
      console.error("Error cargando solicitud:", error);
      showToast({ type: "error", message: "Error cargando la solicitud" });
      navigate('/historial-solicitudes');
    } finally {
      setLoading(false);
    }
  };

  // Formatear fecha
  const formatDate = (timestamp) => {
    if (!timestamp) return "Sin fecha";
    
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Obtener color del estado
  const getStatusColor = (status) => {
    switch (status) {
      case 'enviada': return '#f59e0b';
      case 'procesada': return '#3b82f6';
      case 'completada': return '#10b981';
      default: return '#6b7280';
    }
  };

  // Obtener texto del estado
  const getStatusText = (status) => {
    switch (status) {
      case 'enviada': return 'Enviada';
      case 'procesada': return 'En Proceso';
      case 'completada': return 'Completada';
      default: return 'Desconocido';
    }
  };

  // Imprimir solicitud
  const handlePrint = () => {
    window.print();
  };

  // Eliminar solicitud (solo admins)
  const handleDelete = async () => {
    if (!window.confirm('¿Seguro que quieres eliminar esta solicitud? Esta acción no se puede deshacer.')) return;
    try {
      setDeleting(true);
      await deleteDoc(doc(db, "work_requests", id));
      showToast({ type: "success", message: "Solicitud eliminada correctamente" });
      navigate('/historial-solicitudes');
    } catch (error) {
      console.error("Error eliminando solicitud:", error);
      showToast({ type: "error", message: "Error al eliminar la solicitud" });
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="container">
        <p style={{ padding: 24, textAlign: 'center' }}>Cargando solicitud...</p>
      </div>
    );
  }

  if (!solicitud) {
    return (
      <div className="container">
        <p style={{ padding: 24, textAlign: 'center' }}>Solicitud no encontrada</p>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="header">
        <span className="badge">FIRMAS CEJM</span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <button 
          className="copy" 
          onClick={() => navigate('/historial-solicitudes')}
        >
          ← Volver al Historial
        </button>
        <h1 className="title" style={{ margin: 0, flex: 1 }}>
          📋 Detalle de Solicitud
        </h1>
        <span 
          className="badge"
          style={{ 
            backgroundColor: getStatusColor(solicitud.status),
            color: 'white'
          }}
        >
          {getStatusText(solicitud.status)}
        </span>
      </div>

      <div className="card grid">
        {/* Información del solicitante */}
        <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
          <h3 style={{ margin: '0 0 12px 0', color: '#0f172a', borderBottom: '1px solid #e2e8f0', paddingBottom: 10, fontSize: 15, fontWeight: 600 }}>👤 Información del Solicitante</h3>
          <div className="form-grid-2">
            <div>
              <p className="kicker">Técnico</p>
              <p style={{ margin: 0, fontWeight: 'bold' }}>{solicitud.requesterName}</p>
            </div>
            <div>
              <p className="kicker">Email</p>
              <p style={{ margin: 0 }}>{solicitud.requesterEmail}</p>
            </div>
          </div>
          <div className="form-grid-2" style={{ marginTop: 12 }}>
            <div>
              <p className="kicker">Fecha de solicitud</p>
              <p style={{ margin: 0 }}>{formatDate(solicitud.createdAt)}</p>
            </div>
            {solicitud.emailSentAt && (
              <div>
                <p className="kicker">Email enviado</p>
                <p style={{ margin: 0 }}>{formatDate(solicitud.emailSentAt)}</p>
              </div>
            )}
          </div>
        </div>

        {/* Datos del trabajo */}
        <div>
          <h3 style={{ margin: '0 0 12px 0', color: '#0f172a', borderBottom: '1px solid #e2e8f0', paddingBottom: 10, fontSize: 15, fontWeight: 600 }}>
            🔧 Datos del Trabajo
          </h3>
          
          <div className="form-grid-2">
            <div>
              <p className="kicker">Fechas de actuación</p>
              <p style={{ margin: 0, fontWeight: 'bold' }}>{solicitud.fechasActuacion}</p>
            </div>
            <div>
              <p className="kicker">Código de obra</p>
              <p style={{ margin: 0, fontWeight: 'bold' }}>{solicitud.codigoObra}</p>
            </div>
          </div>

          {(solicitud.numeroOferta || solicitud.numeroPresupuesto) && (
            <div className="form-grid-2" style={{ marginTop: 12 }}>
              {solicitud.numeroOferta && (
                <div>
                  <p className="kicker">Nº Oferta/Pedido</p>
                  <p style={{ margin: 0 }}>{solicitud.numeroOferta}</p>
                </div>
              )}
              {solicitud.numeroPresupuesto && (
                <div>
                  <p className="kicker">Nº Presupuesto</p>
                  <p style={{ margin: 0 }}>{solicitud.numeroPresupuesto}</p>
                </div>
              )}
            </div>
          )}

          <div style={{ marginTop: 12 }}>
            <p className="kicker">Partida de trabajo</p>
            <p style={{ margin: 0, fontWeight: 'bold' }}>{solicitud.partidaTrabajo}</p>
          </div>
        </div>

        {/* Descripción */}
        <div>
          <h3 style={{ margin: '0 0 12px 0', color: '#0f172a', borderBottom: '1px solid #e2e8f0', paddingBottom: 10, fontSize: 15, fontWeight: 600 }}>
            📝 Descripción
          </h3>
          <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
            <p style={{ margin: 0, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
              {solicitud.trabajosRealizar}
            </p>
          </div>
        </div>

        {/* Recursos */}
        <div>
          <h3 style={{ margin: '0 0 12px 0', color: '#0f172a', borderBottom: '1px solid #e2e8f0', paddingBottom: 10, fontSize: 15, fontWeight: 600 }}>
            👥 Recursos
          </h3>
          <div>
            <p className="kicker">Operarios asignados</p>
            <p style={{ margin: 0, fontWeight: 'bold' }}>{solicitud.operariosAsignados}</p>
          </div>
        </div>

        {/* 🛡️ PRL - SI APLICA */}
        {solicitud.solicitarPRL && (
          <div style={{
            background: solicitud.prlCompletado ? '#dcfce7' : '#fef3c7',
            border: `2px solid ${solicitud.prlCompletado ? '#10b981' : '#f59e0b'}`,
            borderRadius: '10px',
            padding: '20px'
          }}>
            <h3 style={{ 
              margin: '0 0 16px 0', 
              color: solicitud.prlCompletado ? '#065f46' : '#92400e',
              fontSize: 18,
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              🛡️ Prevención de Riesgos Laborales (PRL)
              {solicitud.prlCompletado && <span style={{ 
                background: '#10b981', 
                color: 'white', 
                padding: '4px 12px', 
                borderRadius: '12px', 
                fontSize: '12px',
                fontWeight: 'bold'
              }}>✓ COMPLETADO</span>}
            </h3>

            <div style={{ marginBottom: '16px' }}>
              <p className="kicker">Responsable PRL</p>
              <p style={{ margin: 0, fontWeight: 'bold' }}>
                {solicitud.responsablePRL?.nombre}
              </p>
              {solicitud.responsablePRL?.email && (
                <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#6b7280' }}>
                  <a href={`mailto:${solicitud.responsablePRL.email}`} style={{ color: '#4f6cf7', textDecoration: 'none' }}>
                    {solicitud.responsablePRL.email}
                  </a>
                </p>
              )}
            </div>

            {!solicitud.prlCompletado ? (
              <div style={{ textAlign: 'center', padding: '16px 0' }}>
                <p style={{ margin: '0', color: '#92400e', fontWeight: 'bold' }}>
                  ⏳ Pendiente de completar formulario PRL
                </p>
                <p style={{ margin: '8px 0 0 0', fontSize: '13px', color: '#78350f' }}>
                  El responsable PRL recibirá el enlace por email
                </p>
              </div>
            ) : (
              <div>
                <p className="kicker">Completado el: {formatDate(solicitud.prlCompletadoAt)}</p>
                {solicitud.prlDatos?.riesgosIdentificados && (
                  <div style={{ marginBottom: '12px', marginTop: '12px' }}>
                    <p className="kicker">Riesgos identificados</p>
                    <p style={{ margin: 0, whiteSpace: 'pre-wrap', background: 'white', padding: '8px', borderRadius: '4px' }}>{solicitud.prlDatos.riesgosIdentificados}</p>
                  </div>
                )}
                {solicitud.prlDatos?.medidasPreventivas && (
                  <div style={{ marginBottom: '12px' }}>
                    <p className="kicker">Medidas preventivas</p>
                    <p style={{ margin: 0, whiteSpace: 'pre-wrap', background: 'white', padding: '8px', borderRadius: '4px' }}>{solicitud.prlDatos.medidasPreventivas}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Ubicación */}
        <div>
          <h3 style={{ margin: '0 0 12px 0', color: '#0f172a', borderBottom: '1px solid #e2e8f0', paddingBottom: 10, fontSize: 15, fontWeight: 600 }}>
            📍 Ubicación y Contacto
          </h3>
          
          <div style={{ marginBottom: 12 }}>
            <p className="kicker">Dirección de la obra</p>
            <p style={{ margin: 0, fontWeight: 'bold' }}>{solicitud.direccionObra}</p>
          </div>

          <div className="form-grid-2">
            <div>
              <p className="kicker">Persona de contacto</p>
              <p style={{ margin: 0 }}>{solicitud.personaContacto}</p>
            </div>
            {solicitud.telefonoContacto && (
              <div>
                <p className="kicker">Teléfono</p>
                <p style={{ margin: 0 }}>
                  <a href={`tel:${solicitud.telefonoContacto}`} style={{ color: '#4f6cf7', textDecoration: 'none' }}>
                    {solicitud.telefonoContacto}
                  </a>
                </p>
              </div>
            )}
            {solicitud.emailCliente && (
              <div>
                <p className="kicker">Email del cliente</p>
                <p style={{ margin: 0 }}>
                  <a href={`mailto:${solicitud.emailCliente}`} style={{ color: '#4f6cf7', textDecoration: 'none' }}>
                    {solicitud.emailCliente}
                  </a>
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Acciones */}
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', paddingTop: 16, borderTop: '1px solid #e2e8f0' }}>
          <button className="btn" onClick={handlePrint}>🖨️ Imprimir</button>
          
          <button className="copy" onClick={() => navigate('/historial-solicitudes')}>📋 Ver Historial</button>
          
          <button className="copy" onClick={() => navigate('/solicitar-parte')}>➕ Nueva Solicitud</button>

          {/* Crear parte de trabajo */}
          <button 
            className="btn" 
            onClick={() => navigate(`/upload?from=solicitud&requestId=${solicitud.id}&type=parte_trabajo`)}
            style={{ background: '#16a34a', color: '#fff' }}
          >
            🔧 Crear Parte de Trabajo
          </button>

          {/* Editar: usuario si está en "enviada", admin siempre */}
          {canEdit(solicitud) && (
            <button 
              className="copy"
              onClick={() => navigate(`/solicitud/${solicitud.id}/editar`)}
              style={{ borderColor: '#f59e0b', color: '#92400e' }}
            >
              ✏️ Editar
            </button>
          )}

          {/* Eliminar: solo admins */}
          {canDelete && (
            <button 
              className="copy"
              onClick={handleDelete}
              disabled={deleting}
              style={{ borderColor: '#ef4444', color: '#ef4444' }}
            >
              {deleting ? 'Eliminando...' : '🗑️ Eliminar'}
            </button>
          )}

          <div style={{ flex: 1 }}></div>
          <span className="meta">ID: {solicitud.id.slice(0, 8)}...</span>
        </div>
      </div>

      {/* Estilos para impresión */}
      <style jsx>{`
        @media print {
          .header, button, .copy, .btn {
            display: none !important;
          }
          .container {
            max-width: none !important;
            margin: 0 !important;
            padding: 20px !important;
          }
          .card {
            border: none !important;
            box-shadow: none !important;
          }
        }
      `}</style>
    </div>
  );
}